// Session environment for the QoE plane (contract v2, SessionStart): the
// IP-free facts about where the player runs, read once at session start.
//
// Model and OS version come from UA client hints ONLY — the high-entropy
// values a browser is willing to hand over, on request. The raw user agent
// string is never read or sent from here: the ingest already derives
// browser family and major from the transport header and drops it (plan
// D11), and parsing it a second time in the client would put a
// fingerprint-grade string on the wire to no benefit. On a browser without
// client hints (Safari, Firefox) both fields are simply absent.

export type DeviceClass =
  | 'DEVICE_CLASS_PHONE'
  | 'DEVICE_CLASS_TABLET'
  | 'DEVICE_CLASS_DESKTOP'
  | 'DEVICE_CLASS_TV'
  | 'DEVICE_CLASS_OTHER';
export type ConnectionType =
  | 'CONNECTION_TYPE_UNKNOWN'
  | 'CONNECTION_TYPE_WIFI'
  | 'CONNECTION_TYPE_CELLULAR'
  | 'CONNECTION_TYPE_ETHERNET'
  | 'CONNECTION_TYPE_OTHER';
/**
 * The generation of the access network, separate from the bearer above.
 *
 * The web can only ever report the ESTIMATED_ half. `effectiveType` is not
 * a radio reading: it names the cellular generation whose typical
 * performance the connection RESEMBLES, derived from observed rtt and
 * downlink. A fast WiFi link reports '4g', which is exactly why these
 * values may not share a value space with the measured ones the native
 * SDKs send — merged, every Chrome-desktop WiFi session would be charted as
 * cellular traffic that does not exist.
 *
 * There is no ESTIMATED_5G: the API is specified to top out at '4g'. That
 * is a ceiling, not a mapping — a congested 5G link reports '3g', because
 * the value follows throughput rather than the radio.
 */
export type RadioAccess =
  | 'RADIO_ACCESS_ESTIMATED_SLOW_2G'
  | 'RADIO_ACCESS_ESTIMATED_2G'
  | 'RADIO_ACCESS_ESTIMATED_3G'
  | 'RADIO_ACCESS_ESTIMATED_4G';
export type IntegrationKind = 'INTEGRATION_KIND_SDK' | 'INTEGRATION_KIND_IFRAME';

/**
 * Where the player is embedded, and how.
 *
 * Not simply location.origin: inside an iframe that is the PLAYER's own
 * origin, which is the same constant for every hosted-embed session and
 * answers a question nobody asked. The embedding page is what a customer
 * means by "where", so a framed player reports the top-level ancestor —
 * from ancestorOrigins where the browser has it, else the referrer's
 * origin — and says so with INTEGRATION_KIND_IFRAME.
 *
 * A cross-origin embedder that reveals neither leaves the host empty
 * rather than substituting the player's own origin: a wrong attribution
 * is worse than a missing one.
 */
function embedding(): { host?: string; integration: IntegrationKind } {
  if (typeof window === 'undefined') return { integration: 'INTEGRATION_KIND_SDK' };
  let framed: boolean;
  try {
    framed = window.top !== window.self;
  } catch {
    // A cross-origin ancestor can make even the comparison throw, and that
    // it threw is itself proof of being framed.
    framed = true;
  }
  const own = window.location?.origin;
  if (!framed) {
    return { host: own && own !== 'null' ? own : undefined, integration: 'INTEGRATION_KIND_SDK' };
  }
  let host: string | undefined;
  try {
    const ancestors = (window.location as Location & { ancestorOrigins?: DOMStringList })
      .ancestorOrigins;
    // The LAST entry is the top-level document — the page the viewer is
    // actually on; [0] would be an intermediate wrapper frame.
    if (ancestors && ancestors.length > 0) host = ancestors[ancestors.length - 1] ?? undefined;
    if (!host && typeof document !== 'undefined' && document.referrer) {
      host = new URL(document.referrer).origin;
    }
  } catch {
    host = undefined;
  }
  if (host === 'null') host = undefined;
  return { host, integration: 'INTEGRATION_KIND_IFRAME' };
}

export interface SessionEnvironment {
  deviceClass: DeviceClass;
  /** Marketing model name, e.g. "Pixel 8". Empty on desktop and wherever
   *  client hints are unavailable. */
  deviceModel?: string;
  /** Platform version as the browser reports it, e.g. "14.0.0". */
  osVersion?: string;
  screenWidth?: number;
  screenHeight?: number;
  devicePixelRatio?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  connectionType: ConnectionType;
  /** The generation, INDEPENDENT of connectionType: Chrome on Android sets
   *  both, Chrome on desktop sets only this one, Safari and Firefox set
   *  neither. Absent when the browser reports no effectiveType. */
  radioAccess?: RadioAccess;
  downlinkKbps?: number;
  /** Round-trip estimate the browser reports, beside downlinkKbps. Absent
   *  when unreported; never sent as 0, which would read as "measured, and
   *  instant". */
  rttMs?: number;
  saveData?: boolean;
  /** The page the player is embedded in: the document's own origin when the
   *  SDK runs top-level, the TOP-LEVEL ancestor when it runs framed. Absent
   *  when a cross-origin embedder reveals neither. */
  host?: string;
  integration: IntegrationKind;
}

interface UAData {
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platformVersion?: string }>;
}
interface NetInfo {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

/** Coarse form factor from UA client hints where they exist, else from the viewport. */
function deviceClass(): DeviceClass {
  if (typeof navigator === 'undefined') return 'DEVICE_CLASS_OTHER';
  const ua = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  const platform = ua?.platform ?? '';
  if (/tv|tizen|webos/i.test(platform)) return 'DEVICE_CLASS_TV';
  const shortSide = typeof screen !== 'undefined' ? Math.min(screen.width, screen.height) : 0;
  const mobile =
    ua?.mobile ??
    (typeof navigator.maxTouchPoints === 'number' &&
      navigator.maxTouchPoints > 1 &&
      shortSide > 0 &&
      shortSide < 900);
  if (mobile) return shortSide >= 600 ? 'DEVICE_CLASS_TABLET' : 'DEVICE_CLASS_PHONE';
  if (ua || shortSide >= 900 || /mac|win|linux|chrome os/i.test(platform))
    return 'DEVICE_CLASS_DESKTOP';
  return 'DEVICE_CLASS_OTHER';
}

/**
 * The Network Information API, read as TWO independent facts.
 *
 * `type` is the bearer and `effectiveType` is the generation estimate, and
 * a browser may report either, both, or neither — they are not fallbacks
 * for one another. Chrome on Android sets both; Chrome on DESKTOP sets
 * `effectiveType`, `downlink` and `rtt` but leaves `type` undefined; Safari
 * and Firefox implement none of it. Reading only `type` therefore threw
 * away a populated reading on every Chrome-desktop session and reported
 * CONNECTION_TYPE_UNKNOWN beside a perfectly good downlink figure.
 *
 * The two are mapped into two fields rather than one: a WiFi session whose
 * effectiveType is '4g' is WIFI and ESTIMATED_4G, never 4G. See RadioAccess.
 */
function connection(): Pick<
  SessionEnvironment,
  'connectionType' | 'radioAccess' | 'downlinkKbps' | 'rttMs' | 'saveData'
> {
  const c =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: NetInfo }).connection
      : undefined;
  if (!c) return { connectionType: 'CONNECTION_TYPE_UNKNOWN' };
  const map: Record<string, ConnectionType> = {
    wifi: 'CONNECTION_TYPE_WIFI',
    cellular: 'CONNECTION_TYPE_CELLULAR',
    ethernet: 'CONNECTION_TYPE_ETHERNET',
  };
  // The four values the spec defines, and only those: an unrecognised
  // string is left ABSENT rather than mapped to a nearest neighbour, so a
  // future spec value cannot silently land in the wrong bucket.
  const radio: Record<string, RadioAccess> = {
    'slow-2g': 'RADIO_ACCESS_ESTIMATED_SLOW_2G',
    '2g': 'RADIO_ACCESS_ESTIMATED_2G',
    '3g': 'RADIO_ACCESS_ESTIMATED_3G',
    '4g': 'RADIO_ACCESS_ESTIMATED_4G',
  };
  // Own-property lookups, not a bare index. `navigator.connection` is
  // replaceable by the embedding page, so these strings are not guaranteed
  // to be the spec's — and a bare `radio['constructor']` resolves to an
  // INHERITED property of Object.prototype, which is truthy. That would put
  // a non-enum value on the beacon and break the rule directly above: an
  // unrecognised reading stays ABSENT.
  //
  // hasOwnProperty.call, not Object.hasOwn: the global build targets
  // firefox91 and safari15 (vite.global.config.ts), and Object.hasOwn needs
  // Firefox 92 / Safari 15.4. Vite downlevels syntax but does not polyfill
  // built-in methods, so it would be undefined there — and the page that
  // defines its own navigator.connection, which is the whole reason for
  // this guard, is exactly the case that would then throw out of session
  // start.
  const own = <T>(table: Record<string, T>, key: string): T | undefined =>
    Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
  const out: ReturnType<typeof connection> = {
    connectionType: c.type
      ? (own(map, c.type) ?? 'CONNECTION_TYPE_OTHER')
      : 'CONNECTION_TYPE_UNKNOWN',
  };
  if (c.effectiveType) {
    const r = own(radio, c.effectiveType);
    if (r) out.radioAccess = r;
  }
  if (typeof c.downlink === 'number' && c.downlink > 0)
    out.downlinkKbps = Math.round(c.downlink * 1000);
  // Same guard as downlink: an unreported rtt must stay absent, not become
  // a measured zero.
  if (typeof c.rtt === 'number' && c.rtt > 0) out.rttMs = Math.round(c.rtt);
  if (c.saveData) out.saveData = true;
  return out;
}

export function collectEnvironment(video: HTMLVideoElement): SessionEnvironment {
  const where = embedding();
  const env: SessionEnvironment = {
    deviceClass: deviceClass(),
    integration: where.integration,
    ...connection(),
  };
  if (where.host) env.host = where.host;
  if (typeof screen !== 'undefined' && screen.width > 0) {
    env.screenWidth = screen.width;
    env.screenHeight = screen.height;
  }
  if (typeof window !== 'undefined' && window.devicePixelRatio > 0) {
    env.devicePixelRatio = Math.round(window.devicePixelRatio * 100) / 100;
  }
  const rect = video.getBoundingClientRect?.();
  if (rect && rect.width > 0) {
    env.viewportWidth = Math.round(rect.width);
    env.viewportHeight = Math.round(rect.height);
  }
  return env;
}

/**
 * The two high-entropy hints, which the API only exposes asynchronously.
 * Resolves from data the browser already holds, so it settles in well under
 * the window SessionStart waits before it goes out; a browser that declines
 * or lacks the API yields an empty object and the fields stay absent.
 */
export async function collectHighEntropy(): Promise<
  Pick<SessionEnvironment, 'deviceModel' | 'osVersion'>
> {
  const ua =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { userAgentData?: UAData }).userAgentData
      : undefined;
  if (!ua?.getHighEntropyValues) return {};
  try {
    const v = await ua.getHighEntropyValues(['model', 'platformVersion']);
    const out: Pick<SessionEnvironment, 'deviceModel' | 'osVersion'> = {};
    if (v.model) out.deviceModel = v.model;
    if (v.platformVersion) out.osVersion = v.platformVersion;
    return out;
  } catch {
    return {};
  }
}
